import React, { createContext, useCallback, useContext, useState, useEffect } from 'react'
import { makeRequest, showAxiosErrorAsToast } from '../request'
import { Toasts } from '../providers/toast'

interface NotificationsContextValue {
  notificationsEnabled: boolean
  toggleNotifications: () => Promise<boolean>
  sendTestNotification: () => Promise<void>
}

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined)

/**
 * useNotifications is a hook that returns the current notification status and provides functions to enable/disable notifications and send test notifications.
 */
export const useNotifications = (): NotificationsContextValue => {
  const context = useContext(NotificationsContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider')
  }
  return context
}

export const NotificationsProvider: React.FC<{ children: React.ReactNode; vapidPublicKey: string }> = ({
  children,
  vapidPublicKey,
}) => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(false)

  const checkNotificationStatus = useCallback(async () => {
    try {
      const timeoutPromise = new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Timeout')), 1000))
      const registrations = await Promise.race<readonly ServiceWorkerRegistration[] | never>([
        navigator.serviceWorker.getRegistrations(), // this sometimes never finishes, which is why we have a timeout
        timeoutPromise,
      ])
      setNotificationsEnabled(
        Notification.permission === 'granted' &&
          registrations.some(
            (registration) => registration.active && registration.active.scriptURL.includes('/service_worker.js')
          )
      )
    } catch (error) {
      console.error('Error checking notification status:', error)
      setNotificationsEnabled(false)
    }
  }, [])

  useEffect(() => {
    const timeout = setTimeout(checkNotificationStatus, 500)
    return () => clearTimeout(timeout)
  }, [checkNotificationStatus])

  const toggleNotifications = useCallback(async () => {
    if (notificationsEnabled) {
      await disableNotifications()
      Toasts.showToast({ message: 'Notifications disabled. You will no longer receive notifications.' })
      await checkNotificationStatus()
    } else {
      try {
        await installServiceWorker(vapidPublicKey)
        Toasts.showToast({ message: 'Notifications enabled. You will now receive notifications.' })
        await checkNotificationStatus()
      } catch (err) {
        console.error('Error installing service worker', err)
        Toasts.showToast({
          subject: 'Notifications not enabled. Please allow notifications or change your browser settings.',
          message: (err as Error).toString(),
          duration: 600000,
          error: true,
        })
        await disableNotifications()
        await checkNotificationStatus()
        return true
      }
    }
    return false
  }, [notificationsEnabled, checkNotificationStatus])

  const sendTestNotification = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, 10000))
    try {
      await makeRequest('/sendPush', undefined, 'GET')
      Toasts.showToast({ message: 'A test notification was sent' })
    } catch (error) {
      showAxiosErrorAsToast(error)
    }
  }, [])

  return (
    <NotificationsContext.Provider value={{ notificationsEnabled, toggleNotifications, sendTestNotification }}>
      {children}
    </NotificationsContext.Provider>
  )
}

async function disableNotifications(): Promise<void> {
  const registrations = await navigator.serviceWorker.getRegistrations()
  for (const registration of registrations) await registration.unregister()
}

async function installServiceWorker(vapidPublicKey: string): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    throw new Error("Service worker isn't supported")
  }
  if (!('PushManager' in window)) {
    throw new Error("Push notifications aren't supported")
  }

  await navigator.serviceWorker.register('/service_worker.js')
  const registration = await navigator.serviceWorker.ready
  if (!registration.pushManager) {
    throw new Error('PushManager is not available')
  }
  await requestPushPermissions(vapidPublicKey)
}

async function requestPushPermissions(vapidPublicKey: string): Promise<void> {
  const permissionResult = await Notification.requestPermission()
  if (permissionResult !== 'granted') {
    throw new Error('No permission granted for notifications')
  }

  const registration = await navigator.serviceWorker.ready

  const subscribeOptions = {
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  }
  await makeRequest('/pushRegister', await registration.pushManager.subscribe(subscribeOptions))
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}
